import { bundleEnum } from "@/api/classes/integration";
import { type VNode, h } from "vue";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons-vue";

export type AuthDto = {
  accessToken?: string;
  accessTokenExpired?: number;
  id?: string;
};

export type LoginDto = AuthDto & {
  errors: any;
};

export type EmailVerificationDto = {
  errors: any;
};

export type SendVerificationCodeDto = {
  errors: any;
};

export type RegistrationDto = {
  errors: any;
};

export enum UserRole {
  Support,
  Owner,
  Employee,
}

export interface UserDto {
  id: string;
  name: string;
  password: string;
  login: string;
  claims: string;
  role: UserRole;
  partnerId: string;
  registrationDate: string;
  partner: PartnerDto;
  roles: string[];
  position: string;
  fio: string;
}

export interface PartnerDto {
  id: string;
  name: string;
  registrationDate: string;
  wildberriesApiKey: string;
  ozonApiKey: string;
  ozonClientId: string;
  yandexMarketApiKey: string;
  bundle: null | bundleEnum;
  ozonEnabled: boolean;
  yandexMarketEnabled: boolean;
  wildberriesEnabled: boolean;
  isImportedWildberries: boolean;
  isImportedYandexMarket: boolean;
  isImportedOzon: boolean;
  firstSyncResidues: boolean;
  organization: null | string;
  inn: null | string;
  isSyncResiduesOn: boolean;
  chestniyZnakEnabled: boolean;
  isImportedChestniyZnak: boolean;
  chestniyZnakApiKey: string;
  wildberriesTokenExpired: boolean;
  wildberriesTokenExpiredDate: string;
  yandexTokenExpired: boolean;
  ozonTokenExpired: boolean;
  imageLink: string;
  syncResidues2: number;
  isSyncResidues2Enable: boolean;
  syncResidues3: number;
  isSyncResidues3Enable: boolean;
  redistributionServiceEnabled: boolean;
  redistributionServiceApiKey: string;
}

export interface BlobImageDto {
  content: string;
  extension: string;
  contentMetaInfo?: string;
  id?: string;
}

export interface AddMessageDto {
  message?: string | null;
  image?: BlobImageDto;
  chatId?: number | null;
  chatTitle?: string | null;
  department?: string | null;
}

export interface ChatDto {
  count: number;
  chats: ChatBloDto[] | null;
}

export interface ChatBloDto {
  id: number;
  userId?: string | null;
  adminId?: string | null;
  userName?: string | null;
  adminName?: string | null;
  isClosed: boolean;
  createdDate: string;
  title?: string | null;
  lastMessage?: string | null;
  partnerId?: string | null;
  department?: string | null;
  applicationNumber?: string | null;
  isSolved: boolean;
  userWhoSolve: string | null;
  solvedDate: string | null;
}

export interface AdminChatFilterDto {
  freeChats?: boolean | null;
  skip: number;
  take: number;
  forAll?: boolean | null;
  isClosed?: boolean | null;
  isSolved?: boolean | null;
  searchText?: string | null;
  start?: string | null;
  end?: string | null;
  isOrderByDesc: boolean; // сортировка по дате последнего сообщения в чате
}

export interface UserChatFilterDto {
  skip: number;
  take: number;
  allForOwner?: boolean | null;
  isClosed?: boolean | null;
  start?: string | null;
  end?: string | null;
  isOrderByDesc: boolean; // сортировка по дате последнего сообщения в чате
}

export interface ChatIdsFilterDto {
  freeChats?: boolean | null;
  forAll?: boolean | null;
  allForOwner?: boolean | null;
  isClosed?: boolean | null;
  isSolved?: boolean | null;
  isOrderByDesc: boolean; // сортировка по дате последнего сообщения в чате
}

export interface MessageDto {
  count: number;
  messages: GetMessageDto[] | null;
}

export interface GetMessageDto {
  id: number;
  message?: string | null;
  createdDate: string;
  readedDate?: string | null;
  imagePath?: string | null;
  messageAuthorId?: string | null;
  authorName?: string | null;
  chatId: number;
  isRead: boolean;
  type: MessageType;
}

export enum MessageType {
  Regular = 0,
  ServiceAdmin = 1,
}

export enum RequestStatus {
  NEW,
  IN_WORK,
  ANSWERED,
  CLOSED,
}

export const REQUEST_ICON_BY_STATUS: Record<RequestStatus, VNode> = {
  [RequestStatus.CLOSED]: h(CheckCircleOutlined),
  [RequestStatus.NEW]: h(ClockCircleOutlined),
  [RequestStatus.IN_WORK]: h(ClockCircleOutlined),
  [RequestStatus.ANSWERED]: h(ClockCircleOutlined),
};
export const ADMIN_REQUEST_NAME_BY_STATUS: Record<RequestStatus, string> = {
  [RequestStatus.CLOSED]: "Закрыто",
  [RequestStatus.NEW]: "Новое",
  [RequestStatus.IN_WORK]: "В работе",
  [RequestStatus.ANSWERED]: "Решено",
};
export const USER_REQUEST_NAME_BY_STATUS: Record<RequestStatus, string> = {
  [RequestStatus.IN_WORK]: "Открытые обращения",
  [RequestStatus.CLOSED]: "Закрытые обращения",
  [RequestStatus.NEW]: "Новые обращения",
  [RequestStatus.ANSWERED]: "Решенные обращения",
};

export interface SupportUserRequestDTO {
  id: number;
  icon: VNode;
  title: string;
  status: RequestStatus;
  messages: SupportUserMessage[];
}

export interface SupportUserRequest {
  theme: string;
  number: number;
  status: RequestStatus;
  id: string;
  messages: SupportUserMessage[];
}

export interface SupportUserMessage {
  author: string;
  authorId: string | null;
  text: string;
  createdDate: string;
  self: boolean;
  type: MessageType;
  imagePath?: string | null;
}

export interface SupportAdmin {
  id: string;
  name: string;
  fio: string;
  login: string;
}

export interface PaginationConfig {
  current: number;
  pageSize: number;
  defaultPageSize: number;
  showQuickJumper: boolean;
  showSizeChanger: boolean;
  showLessItems: boolean;
  pageSizeOptions: number[];
  showTotal?: (total: number, range: [number, number]) => string;
  total: number;
  onChangePagination: (page: number, pageSize: number) => void | Promise<void>;
}
